import React, { useEffect, useRef, useState } from 'react';
import Header from '../components/header';
import DefaultFooter from '../components/default-footer';

// ============================================================
// religious-tourism.tsx
//
// Premium single page dedicated exclusively to Umrah.
// Rich visual content with real, distinct photography for
// each section. No testimonials - focus on service visuals.
// ============================================================

function DuneRule() {
  return (
    <div aria-hidden="true" style={{ lineHeight: 0 }}>
      <svg viewBox="0 0 1200 34" width="100%" height="22" preserveAspectRatio="none">
        <path d="M0 24 C 90 4, 170 4, 250 20 S 400 30, 480 14 S 620 2, 700 18 S 850 30, 940 12 S 1090 2, 1200 16" fill="none" stroke="var(--gold-d)" strokeOpacity="0.55" strokeWidth="1.5" />
        <path d="M0 28 C 90 12, 170 12, 250 26 S 400 34, 480 20 S 620 10, 700 24 S 850 34, 940 18 S 1090 10, 1200 22" fill="none" stroke="rgba(41,75,90,.14)" strokeWidth="1" />
      </svg>
    </div>
  );
}

// ============================================================
// IMAGES
// Each entry below points to a real, distinct photograph
// (Unsplash License — free to use). Swap for your own brand
// photography whenever it's ready; the crop points are tuned
// for the layouts they appear in.
// ============================================================

const IMAGES = {
  // Hero — Kaaba, wide, during Umrah (Sam Riz / Unsplash)
  hero: 'https://images.unsplash.com/photo-1693590614566-1d3ea9ef32f7?w=1800&q=80&fit=crop&crop=center',

  // Makkah — Holy Kaaba close (mohavic Otb / Unsplash)
  makkah: 'https://images.unsplash.com/photo-1566747675140-317e7484b10d?w=1200&q=80&fit=crop&crop=center',

  // Madinah — Al-Masjid an-Nabawi, green dome skyline (Helmi Tan / Unsplash)
  madinah: 'https://images.unsplash.com/photo-1729931421786-7bbd6c7d78f6?w=1200&q=80&fit=crop&crop=center',

  // Congregation in prayer — used for the Ihram / devotion moment
  ihram: 'https://images.unsplash.com/photo-1575751639353-e292e76daca3?w=1200&q=80&fit=crop&crop=center',

  // Tawaf — pilgrims circling the Kaaba
  tawaf: 'https://images.unsplash.com/photo-1693590614566-1d3ea9ef32f7?w=1200&q=80&fit=crop&crop=center',

  // Masjid an-Nabawi colonnade — used for Sa'i / the mosque corridors
  sai: 'https://images.unsplash.com/photo-1677835238946-20fc43ab0533?w=1200&q=80&fit=crop&crop=center',

  // Luxury hotel exterior near Haram
  hotel: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80&fit=crop&crop=center',

  // Hotel room interior
  hotelInterior: 'https://images.unsplash.com/photo-1631049421450-348ccd7f8949?w=1200&q=80&fit=crop&crop=center',

  // Business class flight
  flight: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80&fit=crop&crop=center',

  // Ground transport — private car
  transport: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=1200&q=80&fit=crop&crop=center',

  // Prayer at the Prophet's Mosque, dusk
  prayer: 'https://images.unsplash.com/photo-1711261817080-8f064416206c?w=1200&q=80&fit=crop&crop=center',

  // Masjid al-Haram, close — used for Zamzam section
  zamzam: 'https://images.unsplash.com/photo-1566747675140-317e7484b10d?w=1200&q=80&fit=crop&crop=center',

  // Nabawi rooftop, Madinah skyline — used for the wider Ziyarat / historic-sites card
  ziyarat: 'https://images.unsplash.com/photo-1710213012556-021eb2a99a49?w=1200&q=80&fit=crop&crop=center',

  // Family Umrah — Kaaba wide, reused for the family package
  family: 'https://images.unsplash.com/photo-1693590614566-1d3ea9ef32f7?w=1200&q=80&fit=crop&crop=center',

  // Oman connection
  omanWadi: 'https://images.unsplash.com/photo-1682695795255-b236b1f1267d?w=800&q=80&fit=crop&crop=center',
  omanFort: 'https://images.unsplash.com/photo-1708417251563-8b3c8a5bca9f?w=800&q=80&fit=crop&crop=center',
};

// ============================================================
// DATA
// ============================================================

interface Stat {
  value: string;
  label: string;
}

interface Package {
  id: string;
  name: string;
  description: string;
  features: string[];
  price: string;
  duration: string;
  image: string;
  badge?: 'Popular' | 'Premium' | 'Luxury';
}

interface ServiceImage {
  id: string;
  title: string;
  description: string;
  image: string;
}

interface RitualStep {
  n: string;
  title: string;
  arabic: string;
  description: string;
}

interface Inclusion {
  title: string;
  description: string;
  icon: 'flight' | 'hotel' | 'visa' | 'transport' | 'guide' | 'support' | 'zamzam' | 'kit';
}

interface Faq {
  q: string;
  a: string;
}

const stats: Stat[] = [
  { value: '15+', label: 'Years of Experience' },
  { value: '8,500+', label: 'Umrah Pilgrims Served' },
  { value: '99%', label: 'Satisfaction Rate' },
  { value: '7', label: 'Premium Hotels Near Haram' },
];

const trustBadges = [
  'Licensed Umrah Operator',
  'IATA-Accredited Agency',
  '24/7 Pilgrim Support',
  'Nusuk-Registered Partner',
];

const rituals: RitualStep[] = [
  {
    n: '01',
    title: 'Ihram',
    arabic: 'الإحرام',
    description:
      "Before crossing the Miqat, you enter the sacred state of Ihram: two unstitched white garments for men, modest dress for women, and a sincere intention (niyyah) to perform Umrah for the sake of Allah.",
  },
  {
    n: '02',
    title: 'Tawaf',
    arabic: 'الطواف',
    description:
      'Seven counter-clockwise circuits around the Kaaba, beginning and ending at the Black Stone corner — the heart of the pilgrimage, performed in humility and remembrance.',
  },
  {
    n: '03',
    title: "Sa'i",
    arabic: 'السعي',
    description:
      "Seven passes between the hills of Safa and Marwa, inside the mosque's climate-controlled gallery, retracing Hajar's search for water and trust in divine provision.",
  },
  {
    n: '04',
    title: 'Halq / Taqsir',
    arabic: 'الحلق أو التقصير',
    description:
      'Hair is shaved or trimmed to mark the completion of Umrah, formally ending the state of Ihram and the rites of the journey.',
  },
];

const inclusions: Inclusion[] = [
  { title: 'Return Flights', description: 'Direct or connecting flights from Oman, ticketed and confirmed before departure.', icon: 'flight' },
  { title: 'Visa Processing', description: 'Full Umrah visa handling, with document checks specific to your passport.', icon: 'visa' },
  { title: 'Hotel Accommodation', description: 'Vetted 4- and 5-star hotels within walking distance of both Haramain.', icon: 'hotel' },
  { title: 'Private Transfers', description: 'Air-conditioned transport between airport, hotels, and both holy cities.', icon: 'transport' },
  { title: 'Guided Ziyarat', description: 'A knowledgeable guide accompanies every historical and religious site visit.', icon: 'guide' },
  { title: '24/7 Support Team', description: 'A dedicated coordinator reachable throughout your trip, in Arabic and English.', icon: 'support' },
  { title: 'Zamzam Water', description: 'A supply of Zamzam water arranged for your return journey home.', icon: 'zamzam' },
  { title: 'Ihram Essentials', description: "A starter Ihram kit provided for men ahead of the Miqat.", icon: 'kit' },
];

const faqs: Faq[] = [
  {
    q: 'Do I need a Mahram to travel?',
    a: "Requirements depend on age, nationality, and current Saudi regulations, which are updated from time to time. Our team confirms the latest guidance for your specific passport before you book, so you always travel with accurate, up-to-date information.",
  },
  {
    q: 'What documents will I need?',
    a: 'A passport valid for at least six months, recent passport photographs, and — depending on your nationality — proof of vaccination. We provide a personalised checklist once your package is confirmed.',
  },
  {
    q: 'When is the best time to go?',
    a: 'Umrah can be performed year-round outside the Hajj season. Cooler months (November–February) are more comfortable for walking and standing; Ramadan is spiritually significant but considerably busier and higher in price.',
  },
  {
    q: 'Can a package be customised?',
    a: 'Yes. Duration, hotel category, flight cabin, and the balance of time between Makkah and Madinah can all be adjusted. Tell us what matters most and we will tailor a quote around it.',
  },
  {
    q: 'Is the trip suitable for elderly or less mobile travellers?',
    a: "We can arrange wheelchair assistance, ground-floor or elevator-accessible rooms, and a slower-paced itinerary. Let us know any mobility needs when you enquire so we can plan accordingly.",
  },
  {
    q: 'What is your payment and cancellation policy?',
    a: 'A deposit secures your booking, with the balance due closer to departure. Full terms, including cancellation windows, are shared in writing before you commit — nothing is charged without your sign-off.',
  },
];

const packages: Package[] = [
  {
    id: 'silver',
    name: 'Silver Umrah',
    description: 'A comfortable and thoughtfully arranged Umrah experience with quality accommodation and dedicated support throughout your journey.',
    image: IMAGES.makkah,
    features: [
      'Return flights from Oman',
      '4-star hotels near Haram (Makkah & Madinah)',
      'Private airport transfers',
      'Visa processing included',
      'Local guidance & support team',
      'Ziyarat tours in Makkah & Madinah',
    ],
    price: 'From OMR 1,450',
    duration: '7 nights / 8 days',
  },
  {
    id: 'gold',
    name: 'Gold Umrah',
    description: 'Premium Umrah experience with enhanced comfort, superior hotel locations, and personalized services for a truly peaceful pilgrimage.',
    image: IMAGES.hotel,
    features: [
      'Return flights from Oman (Business Class upgrade available)',
      'Luxury hotels steps from Haram',
      'Private VIP airport transfers',
      'Visa processing & fast-track immigration',
      'Personal guide for rituals and Ziyarat',
      '24/7 dedicated concierge service',
      'Premium Zamzam water delivery',
    ],
    price: 'From OMR 2,850',
    duration: '10 nights / 11 days',
    badge: 'Premium',
  },
  {
    id: 'family',
    name: 'Family Umrah',
    description: 'Specially designed for families traveling together — with child-friendly hotels, flexible scheduling, and extra care for every member.',
    image: IMAGES.family,
    features: [
      'Return flights for whole family',
      'Family suites near Haram',
      'Child-friendly meals & amenities',
      'Private transport with child seats',
      'Visa processing for all family members',
      "Flexible itinerary for children's needs",
      '24/7 family support team',
    ],
    price: 'From OMR 3,200',
    duration: '8 nights / 9 days',
    badge: 'Popular',
  },
];

const serviceImages: ServiceImage[] = [
  {
    id: 'makkah',
    title: 'Makkah Al-Mukarramah',
    description: 'The heart of the Islamic world — the Holy Haram, where millions gather in prayer and devotion.',
    image: IMAGES.makkah,
  },
  {
    id: 'madinah',
    title: 'Madinah Al-Munawwarah',
    description: "The city of the Prophet ﷺ — a place of peace, serenity, and spiritual reflection.",
    image: IMAGES.madinah,
  },
  {
    id: 'tawaf',
    title: 'Tawaf Around the Kaaba',
    description: "The sacred circumambulation — seven circles around the House of Allah, the heart of every pilgrim's journey.",
    image: IMAGES.tawaf,
  },
  {
    id: 'sai',
    title: "Sa'i Between Safa & Marwa",
    description: "Following the footsteps of Hajar — a ritual of faith, perseverance, and divine trust.",
    image: IMAGES.sai,
  },
  {
    id: 'ihram',
    title: 'The State of Ihram',
    description: 'A state of purity and devotion — pilgrims enter Ihram with the intention of complete submission to Allah.',
    image: IMAGES.ihram,
  },
  {
    id: 'prayer',
    title: "Prayer at the Prophet's Mosque",
    description: 'A moment of profound peace — offering prayers in the blessed city of Madinah.',
    image: IMAGES.prayer,
  },
  {
    id: 'hotel',
    title: 'Premium Hotels Near Haram',
    description: 'Stay steps away from the Haram — comfortable accommodation designed for rest between prayers.',
    image: IMAGES.hotel,
  },
  {
    id: 'zamzam',
    title: 'Zamzam Water',
    description: 'The blessed water drawn from within Masjid al-Haram — offered freely to every pilgrim who visits.',
    image: IMAGES.zamzam,
  },
  {
    id: 'ziyarat',
    title: 'Ziyarat in Madinah',
    description: "Guided visits to the historic and religious sites around Madinah, woven into your itinerary.",
    image: IMAGES.ziyarat,
  },
];

// ============================================================
// ICONS (inline, single-colour, inherit currentColor)
// ============================================================

function Icon({ name }: { name: Inclusion['icon'] }) {
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'flight':
      return <svg {...common}><path d="M2 16l20-7-7 20-3-8-8-3z" /><path d="M15 9l-6 6" /></svg>;
    case 'hotel':
      return <svg {...common}><path d="M3 21V8l9-5 9 5v13" /><path d="M9 21v-7h6v7" /></svg>;
    case 'visa':
      return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M7 9h4M7 13h7" /><circle cx="17" cy="14" r="1.4" /></svg>;
    case 'transport':
      return <svg {...common}><path d="M3 13l1.6-5A2 2 0 0 1 6.5 6.5h11a2 2 0 0 1 1.9 1.5L21 13" /><rect x="3" y="13" width="18" height="6" rx="1.5" /><circle cx="7.5" cy="19" r="1.3" /><circle cx="16.5" cy="19" r="1.3" /></svg>;
    case 'guide':
      return <svg {...common}><circle cx="12" cy="8" r="3" /><path d="M5 21c0-4 3-6.5 7-6.5S19 17 19 21" /></svg>;
    case 'support':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M8 15c1.2 1 2.6 1.5 4 1.5s2.8-.5 4-1.5" /><path d="M9 10h.01M15 10h.01" /></svg>;
    case 'zamzam':
      return <svg {...common}><path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z" /></svg>;
    case 'kit':
      return <svg {...common}><path d="M12 3v6M12 9c-3 0-5 2-5 5v7h10v-7c0-3-2-5-5-5z" /></svg>;
    default:
      return null;
  }
}

// ============================================================
// COMPONENT
// ============================================================

export default function ReligiousTourism() {
  const statsRef = useRef<HTMLDivElement>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.3 }
    );

    const revealItems = document.querySelectorAll('.stat-item, .reveal-item');
    revealItems.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
          --radius: 10px;
          --shadow-sm: 0 6px 20px rgba(26, 49, 61, .07);
          --shadow-md: 0 20px 48px rgba(26, 49, 61, .12);
          --shadow-lg: 0 28px 64px rgba(26, 49, 61, .18);
        }

        * { box-sizing: border-box; }
        .visa-page { font-family: 'DM Sans', sans-serif; background: var(--cream); -webkit-font-smoothing: antialiased; }
        .visa-page h1, .visa-page h2, .visa-page h3 { font-family: 'Playfair Display', serif; }

        .eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-d); }
        .eyebrow::before { content: ''; width: 22px; height: 2px; background: var(--gold); border-radius: 2px; }
        .eyebrow.light { color: var(--gold-l); }
        .eyebrow.light::before { background: var(--gold-l); opacity: .6; }
        .eyebrow.centered { justify-content: center; }
        .eyebrow.centered::before { margin-right: 2px; }

        .section-title { font-size: clamp(1.7rem, 2.6vw, 2.4rem); font-weight: 500; color: var(--ink); line-height: 1.2; margin: 10px 0 12px; }
        .section-title.centered { text-align: center; }
        .section-sub { font-size: 15px; color: var(--silver); font-weight: 300; line-height: 1.7; max-width: 520px; }
        .section-sub.centered { margin: 0 auto; text-align: center; }
        .section-pad { padding: 84px 0; }
        @media (max-width: 991px) { .section-pad { padding: 64px 0; } }
        @media (max-width: 576px) { .section-pad { padding: 48px 0; } }
        .bg-cream { background: var(--cream); }
        .bg-white { background: #fff; }
        .bg-teal { background: var(--teal-d); }

        .vbtn-primary { display: inline-flex; align-items: center; gap: 8px; background: var(--gold); color: var(--teal-d); border-radius: 5px; font-size: 12.5px; font-weight: 700; letter-spacing: .3px; padding: 14px 26px; text-decoration: none; transition: transform .2s, background .2s; box-shadow: 0 10px 28px rgba(211,190,118,.28); border: none; cursor: pointer; }
        .vbtn-primary:hover { background: #e2ce86; transform: translateY(-2px); }
        .vbtn-ghost { display: inline-flex; align-items: center; background: rgba(255,255,255,.1); backdrop-filter: blur(4px); color: #fff; border: 1px solid rgba(255,255,255,.25); border-radius: 5px; font-size: 12.5px; font-weight: 600; letter-spacing: .3px; padding: 14px 24px; text-decoration: none; transition: background .2s, border-color .2s; }
        .vbtn-ghost:hover { border-color: #fff; background: rgba(255,255,255,.18); }
        .vbtn-outline { display: inline-flex; align-items: center; justify-content: center; gap: 8px; background: transparent; color: var(--teal-d); border: 1.5px solid var(--gold); border-radius: 5px; font-size: 12.5px; font-weight: 600; letter-spacing: .3px; padding: 12px 22px; text-decoration: none; transition: background .2s, transform .2s; cursor: pointer; width: 100%; }
        .vbtn-outline:hover { background: var(--gold); transform: translateY(-2px); }

        /* HERO */
        .rt-hero { position: relative; min-height: 560px; display: flex; align-items: center; overflow: hidden; background: var(--teal-d); }
        .rt-hero-img { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: 0.32; }
        .rt-hero-veil { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(26,49,61,.55) 0%, rgba(26,49,61,.92) 100%); }
        .rt-hero-inner { position: relative; z-index: 2; padding: 64px 0 40px; }
        .rt-hero-title { font-weight: 600; color: #fff; line-height: 1.16; font-size: clamp(32px, 4.2vw, 52px); max-width: 700px; margin-bottom: 16px; }
        .rt-hero-title .gold { font-style: italic; color: var(--gold); font-weight: 400; }
        .rt-hero-sub { font-size: 15px; color: rgba(255,255,255,.78); font-weight: 300; line-height: 1.75; max-width: 540px; margin-bottom: 30px; }
        .rt-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 34px; }
        .rt-hero-badges { display: flex; flex-wrap: wrap; gap: 10px 26px; padding-top: 22px; border-top: 1px solid rgba(255,255,255,.14); }
        .rt-hero-badges span { display: inline-flex; align-items: center; gap: 8px; font-size: 11.5px; letter-spacing: .3px; color: rgba(255,255,255,.68); font-weight: 400; }
        .rt-hero-badges span::before { content: ''; width: 5px; height: 5px; border-radius: 50%; background: var(--gold); flex: none; }

        /* TALBIYAH STRIP */
        .talbiyah-strip { background: var(--teal-d); padding: 22px 0; text-align: center; }
        .talbiyah-strip .arabic { font-family: 'Playfair Display', serif; font-size: 1.15rem; color: var(--gold-l); direction: rtl; }
        .talbiyah-strip .translation { font-size: 11.5px; color: rgba(255,255,255,.55); letter-spacing: .4px; margin-top: 4px; font-weight: 300; }

        /* ATMOSPHERE - Full width image block */
        .atmosphere-block {
          position: relative;
          border-radius: var(--radius);
          overflow: hidden;
          min-height: 380px;
          background-size: cover;
          background-position: center;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 40px;
        }
        .atmosphere-block .overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(26,49,61,.75) 0%, rgba(26,49,61,.9) 100%);
        }
        .atmosphere-block .content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 720px;
        }
        .atmosphere-block .content h2 { color: #fff; font-size: clamp(1.6rem, 2.6vw, 2.4rem); font-weight: 500; margin-bottom: 12px; }
        .atmosphere-block .content p { color: rgba(255,255,255,.85); font-size: 15px; font-weight: 300; line-height: 1.8; }

        /* STATS */
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px; }
        @media (max-width: 768px) { .stats-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 480px) { .stats-grid { grid-template-columns: 1fr; } }
        .stat-item {
          text-align: center;
          padding: 20px;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity .8s ease, transform .8s ease;
        }
        .stat-item.visible { opacity: 1; transform: translateY(0); }
        .stat-item .value { font-family: 'Playfair Display', serif; font-size: 2.6rem; font-weight: 600; color: var(--gold); line-height: 1.1; }
        .stat-item .label { font-size: 13px; color: rgba(255,255,255,.7); font-weight: 300; margin-top: 4px; }

        /* REVEAL (generic scroll-in) */
        .reveal-item { opacity: 0; transform: translateY(18px); transition: opacity .7s ease, transform .7s ease; }
        .reveal-item.visible { opacity: 1; transform: translateY(0); }

        /* RITUALS / PROCESS */
        .rituals-rail { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; position: relative; }
        @media (max-width: 900px) { .rituals-rail { grid-template-columns: 1fr 1fr; gap: 28px; } }
        @media (max-width: 560px) { .rituals-rail { grid-template-columns: 1fr; } }
        .rituals-rail::before {
          content: '';
          position: absolute;
          top: 26px; left: 6%; right: 6%;
          height: 1px;
          background: repeating-linear-gradient(90deg, var(--gold-d) 0 8px, transparent 8px 16px);
          opacity: .5;
        }
        @media (max-width: 900px) { .rituals-rail::before { display: none; } }
        .ritual-card { position: relative; padding: 0 22px; text-align: left; }
        .ritual-card:first-child { padding-left: 0; }
        .ritual-num {
          position: relative; z-index: 1;
          width: 52px; height: 52px; border-radius: 50%;
          background: var(--teal-d); color: var(--gold-l);
          font-family: 'Playfair Display', serif; font-size: 1.05rem; font-weight: 600;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 18px; border: 1px solid var(--gold-d);
        }
        .ritual-card h3 { font-size: 1.05rem; font-weight: 600; color: var(--ink); margin-bottom: 2px; display: inline-block; }
        .ritual-card .arabic-tag { font-family: 'Playfair Display', serif; font-size: 13px; color: var(--gold-d); margin-left: 8px; direction: rtl; display: inline-block; }
        .ritual-card p { font-size: 13px; color: var(--silver); font-weight: 300; line-height: 1.7; margin-top: 8px; }

        /* SERVICE GALLERY - Grid of images */
        .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        @media (max-width: 992px) { .gallery-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 640px) { .gallery-grid { grid-template-columns: 1fr; } }

        .gallery-item {
          position: relative;
          border-radius: var(--radius);
          overflow: hidden;
          min-height: 300px;
          background-size: cover;
          background-position: center;
          box-shadow: var(--shadow-sm);
          transition: transform .4s cubic-bezier(.22,1,.36,1), box-shadow .4s cubic-bezier(.22,1,.36,1);
          border: 1px solid rgba(41,75,90,.06);
        }
        .gallery-item:hover { transform: translateY(-6px) scale(1.01); box-shadow: var(--shadow-lg); }
        .gallery-item .g-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(0deg, rgba(26,49,61,.85) 0%, rgba(26,49,61,.2) 60%);
          transition: background .3s ease;
        }
        .gallery-item:hover .g-overlay { background: linear-gradient(0deg, rgba(26,49,61,.9) 0%, rgba(26,49,61,.3) 60%); }
        .gallery-item .g-content {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 24px 22px;
          z-index: 2;
        }
        .gallery-item .g-content h3 { color: #fff; font-size: 1.1rem; font-weight: 600; margin-bottom: 4px; }
        .gallery-item .g-content p { color: rgba(255,255,255,.8); font-size: 12.5px; font-weight: 300; line-height: 1.6; margin: 0; }

        /* INCLUSIONS */
        .inclusion-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: rgba(41,75,90,.08); border: 1px solid rgba(41,75,90,.08); border-radius: var(--radius); overflow: hidden; }
        @media (max-width: 900px) { .inclusion-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 520px) { .inclusion-grid { grid-template-columns: 1fr; } }
        .inclusion-cell { background: #fff; padding: 28px 24px; }
        .inclusion-cell .ic-icon { width: 42px; height: 42px; border-radius: 50%; background: var(--cream); color: var(--gold-d); display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
        .inclusion-cell h3 { font-size: 14.5px; font-weight: 600; color: var(--ink); margin-bottom: 6px; }
        .inclusion-cell p { font-size: 12.5px; color: var(--silver); font-weight: 300; line-height: 1.65; margin: 0; }

        /* PACKAGE CARDS */
        .package-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; align-items: stretch; }
        @media (max-width: 992px) { .package-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 640px) { .package-grid { grid-template-columns: 1fr; } }

        .package-card {
          background: #fff;
          border-radius: var(--radius);
          overflow: hidden;
          box-shadow: var(--shadow-sm);
          border: 1px solid rgba(41,75,90,.06);
          transition: transform .4s cubic-bezier(.22,1,.36,1), box-shadow .4s cubic-bezier(.22,1,.36,1);
          display: flex;
          flex-direction: column;
          position: relative;
        }
        .package-card:hover { transform: translateY(-8px); box-shadow: var(--shadow-lg); }
        .package-card .badge {
          position: absolute; top: 16px; right: 16px; z-index: 2;
          background: var(--gold); color: var(--teal-d);
          font-size: 10px; font-weight: 700; letter-spacing: .5px;
          padding: 4px 14px; border-radius: 20px;
          text-transform: uppercase;
        }
        .package-card .badge.popular { background: var(--teal); color: #fff; }
        .package-card-img { height: 200px; background-size: cover; background-position: center; }
        .package-card-body { padding: 24px 26px 28px; flex: 1; display: flex; flex-direction: column; }
        .package-card-body h3 { font-size: 1.2rem; font-weight: 600; color: var(--ink); margin-bottom: 2px; }
        .package-card-body .duration { font-size: 12px; color: var(--silver); font-weight: 400; margin-bottom: 6px; display: block; }
        .package-card-body .price { font-family: 'Playfair Display', serif; font-size: 1.3rem; font-weight: 600; color: var(--gold-d); margin: 6px 0 10px; }
        .package-card-body p { font-size: 13px; color: var(--silver); font-weight: 300; line-height: 1.6; margin-bottom: 12px; flex: 1; }
        .package-features { list-style: none; padding: 0; margin: 0 0 16px; }
        .package-features li { font-size: 12px; color: var(--teal); font-weight: 400; padding: 3px 0; display: flex; align-items: center; gap: 8px; }
        .package-features li::before { content: '✓'; color: var(--gold); font-weight: 700; }

        /* FAQ */
        .faq-list { max-width: 760px; margin: 0 auto; }
        .faq-item { border-bottom: 1px solid rgba(41,75,90,.1); }
        .faq-item:first-child { border-top: 1px solid rgba(41,75,90,.1); }
        .faq-q {
          width: 100%; background: none; border: none; text-align: left; cursor: pointer;
          display: flex; align-items: center; justify-content: space-between; gap: 20px;
          padding: 22px 4px; font-family: 'Playfair Display', serif; font-size: 1.02rem;
          font-weight: 500; color: var(--ink);
        }
        .faq-q .plus { flex: none; width: 22px; height: 22px; border-radius: 50%; border: 1.5px solid var(--gold-d); position: relative; transition: transform .3s ease, background .3s ease; }
        .faq-q .plus::before, .faq-q .plus::after { content: ''; position: absolute; background: var(--gold-d); top: 50%; left: 50%; transform: translate(-50%,-50%); }
        .faq-q .plus::before { width: 9px; height: 1.4px; }
        .faq-q .plus::after { width: 1.4px; height: 9px; transition: opacity .2s ease; }
        .faq-item.open .plus { background: var(--gold-d); transform: rotate(180deg); }
        .faq-item.open .plus::before, .faq-item.open .plus::after { background: #fff; }
        .faq-item.open .plus::after { opacity: 0; }
        .faq-a { max-height: 0; overflow: hidden; transition: max-height .35s ease; }
        .faq-item.open .faq-a { max-height: 240px; }
        .faq-a p { font-size: 13.5px; color: var(--silver); font-weight: 300; line-height: 1.75; padding: 0 4px 24px; max-width: 640px; }

        /* OMAN CONNECTION */
        .oman-connection { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
        @media (max-width: 800px) { .oman-connection { grid-template-columns: 1fr; } }
        .oman-connection .text-content p { font-size: 14px; color: var(--silver); font-weight: 300; line-height: 1.8; margin-top: 10px; }
        .oman-connection .image-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .oman-connection .image-grid .img { height: 200px; border-radius: var(--radius); background-size: cover; background-position: center; box-shadow: var(--shadow-sm); }
        .oman-connection .image-grid .img.offset { margin-top: 28px; }

        /* CTA BANNER */
        .cta-banner { background: var(--teal); border-radius: var(--radius); padding: 44px 40px; display: flex; align-items: center; justify-content: space-between; gap: 30px; flex-wrap: wrap; }
        .cta-banner-title { color: #fff; font-size: 1.3rem; font-weight: 500; margin-bottom: 4px; }
        .cta-banner-sub { color: rgba(255,255,255,.7); font-size: 13px; font-weight: 300; max-width: 460px; }

        @media (max-width: 576px) {
          .cta-banner { padding: 28px 20px; }
          .package-card-body { padding: 18px 18px 22px; }
          .atmosphere-block { padding: 40px 24px; min-height: 280px; }
          .gallery-item { min-height: 220px; }
          .ritual-card { padding: 0; }
        }
      `}</style>

      <div className="visa-page">
        <Header />

        {/* ===== HERO ===== */}
        <section className="rt-hero">
          <div className="rt-hero-img" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
          <div className="rt-hero-veil" />
          <div className="container rt-hero-inner">
            <div className="eyebrow light" style={{ marginBottom: 14 }}>
              Umrah — The Lesser Pilgrimage
            </div>
            <h1 className="rt-hero-title">
              A Sacred Journey to <span className="gold">Makkah & Madinah</span>
            </h1>
            <p className="rt-hero-sub">
              Umrah is a spiritual journey that calls to the heart of every Muslim. We are here to
              ensure that every step — from your departure to your return — is handled with the
              utmost care, respect, and precision. Let us carry the logistics so you can carry the
              prayer.
            </p>
            <div className="rt-hero-ctas">
              <a href="#packages" className="vbtn-primary">Explore Umrah Packages</a>
              <a href="#gallery" className="vbtn-ghost">View Sacred Sites</a>
            </div>
            <div className="rt-hero-badges">
              {trustBadges.map((b) => <span key={b}>{b}</span>)}
            </div>
          </div>
        </section>

        <div className="talbiyah-strip">
          <div className="arabic">لَبَّيْكَ اللَّهُمَّ لَبَّيْكَ</div>
          <div className="translation">HERE I AM, O ALLAH, HERE I AM — THE PILGRIM'S CALL AT THE MIQAT</div>
        </div>

        <DuneRule />

        {/* ===== ATMOSPHERE ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div
              className="atmosphere-block"
              style={{ backgroundImage: `url(${IMAGES.ihram})` }}
            >
              <div className="overlay" />
              <div className="content">
                <span className="eyebrow light" style={{ justifyContent: 'center' }}>
                  A Pilgrimage of the Heart
                </span>
                <h2>Your Umrah, Arranged With Reverence</h2>
                <p>
                  Umrah is more than travel — it is a spiritual calling. We understand the profound
                  significance of this journey. Our dedicated team handles every detail with precision
                  and care, from visa processing and flights to premium accommodation and ground
                  transport. So you can focus entirely on your worship, reflection, and the sacred
                  moments that matter most.
                </p>
              </div>
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== STATS ===== */}
        <section className="section-pad bg-teal">
          <div className="container">
            <div className="stats-grid" ref={statsRef}>
              {stats.map((s, idx) => (
                <div className="stat-item" key={idx} style={{ transitionDelay: `${idx * 120}ms` }}>
                  <div className="value">{s.value}</div>
                  <div className="label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== RITUALS / THE JOURNEY ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div style={{ marginBottom: 52 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                The Rites, in Order
              </span>
              <h2 className="section-title centered">The Four Rituals of Umrah</h2>
              <p className="section-sub centered">
                Umrah follows a fixed sequence. Our guides walk beside you through each stage, so
                you can focus fully on the meaning behind every step.
              </p>
            </div>
            <div className="rituals-rail">
              {rituals.map((r) => (
                <div className="ritual-card reveal-item" key={r.n}>
                  <div className="ritual-num">{r.n}</div>
                  <h3>{r.title}</h3>
                  <span className="arabic-tag">{r.arabic}</span>
                  <p>{r.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== SERVICE GALLERY ===== */}
        <section className="section-pad bg-cream" id="gallery">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Sacred Sites & Services
              </span>
              <h2 className="section-title centered">The Umrah Experience in Images</h2>
              <p className="section-sub centered">
                From the Kaaba to the Prophet's Mosque — every moment of your Umrah is a moment of
                spiritual connection. Here is a glimpse of what awaits you.
              </p>
            </div>
            <div className="gallery-grid">
              {serviceImages.map((item) => (
                <div
                  className="gallery-item"
                  key={item.id}
                  style={{ backgroundImage: `url(${item.image})` }}
                >
                  <div className="g-overlay" />
                  <div className="g-content">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== INCLUSIONS ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                What's Handled For You
              </span>
              <h2 className="section-title centered">Included in Every Package</h2>
              <p className="section-sub centered">
                A baseline of care that never changes, whichever package you choose. Higher tiers
                add comfort and flexibility on top of this foundation.
              </p>
            </div>
            <div className="inclusion-grid">
              {inclusions.map((inc) => (
                <div className="inclusion-cell" key={inc.title}>
                  <div className="ic-icon"><Icon name={inc.icon} /></div>
                  <h3>{inc.title}</h3>
                  <p>{inc.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== PACKAGES ===== */}
        <section className="section-pad bg-cream" id="packages">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Umrah Packages
              </span>
              <h2 className="section-title centered">Choose the Journey That Fits Your Heart</h2>
            </div>
            <div className="package-grid">
              {packages.map((pkg) => (
                <div className="package-card" key={pkg.id}>
                  {pkg.badge && <span className={`badge ${pkg.badge.toLowerCase()}`}>{pkg.badge}</span>}
                  <div className="package-card-img" style={{ backgroundImage: `url(${pkg.image})` }} />
                  <div className="package-card-body">
                    <h3>{pkg.name}</h3>
                    <span className="duration">📅 {pkg.duration}</span>
                    <div className="price">{pkg.price}</div>
                    <p>{pkg.description}</p>
                    <ul className="package-features">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                    <a href="/contact" className="vbtn-outline">Enquire About This Package</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== FAQ ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div style={{ marginBottom: 36 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Before You Book
              </span>
              <h2 className="section-title centered">Frequently Asked Questions</h2>
            </div>
            <div className="faq-list">
              {faqs.map((f, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div className={`faq-item${isOpen ? ' open' : ''}`} key={f.q}>
                    <button
                      type="button"
                      className="faq-q"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      aria-expanded={isOpen}
                    >
                      <span>{f.q}</span>
                      <span className="plus" />
                    </button>
                    <div className="faq-a">
                      <p>{f.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== OMAN CONNECTION ===== */}
        <section className="section-pad bg-cream">
          <div className="container">
            <div className="oman-connection">
              <div className="text-content">
                <span className="eyebrow">From Oman, With Care</span>
                <h2 className="section-title">Your Umrah Begins Here</h2>
                <p>
                  Every Umrah journey we arrange begins from our home in Oman. The same warmth,
                  precision, and quiet attentiveness that defines Omani hospitality is what we
                  bring to every pilgrimage we organize.
                </p>
                <p style={{ marginTop: 10 }}>
                  We understand the depth of this calling. Let us help you answer it with grace
                  and complete peace of mind.
                </p>
              </div>
              <div className="image-grid">
                <div className="img" style={{ backgroundImage: `url(${IMAGES.omanWadi})` }} />
                <div className="img offset" style={{ backgroundImage: `url(${IMAGES.omanFort})` }} />
              </div>
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== FINAL CTA ===== */}
        <section className="section-pad bg-white" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="cta-banner">
              <div>
                <div className="cta-banner-title">Begin Your Umrah Journey</div>
                <div className="cta-banner-sub">
                  Tell us your preferred dates and we will craft an Umrah experience that honors
                  your faith and your heart.
                </div>
              </div>
              <a href="/contact" className="vbtn-primary">
                Plan Your Umrah
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        <DefaultFooter />
      </div>
    </>
  );
}